import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import TaxCalculator from '../components/TaxCalculator';
import { calculateTakeHomePay } from '../utils/taxCalculations';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const SalaryPage = () => {
  const { amount } = useParams();
  const salary = parseInt(amount);

  // Validate salary amount - check if valid
  const isValidSalary = !isNaN(salary) && salary > 0 && salary <= 1000000;

  useEffect(() => {
    // Only update meta tags if salary is valid
    if (!isValidSalary) return;
    
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const englandResults = calculateTakeHomePay({
      annualSalary: salary,
      region: 'england',
      pensionPercent: 0,
      studentLoanPlan: 'none'
    });

    updateMetaTags({
      title: `${formatCurrency(salary)} Salary After Tax UK | Take-Home Pay Calculator 2025/26`,
      description: `${formatCurrency(salary)} salary after tax in the UK: Take-home pay is ${formatCurrency(englandResults.netPay.annual)} annually or ${formatCurrency(englandResults.netPay.monthly)} per month after Income Tax and National Insurance for 2025/26 tax year.`,
      keywords: `${salary} salary after tax, ${salary} take-home pay, UK salary calculator ${salary}, ${salary} after tax UK`,
      ogTitle: `${formatCurrency(salary)} Salary After Tax UK (2025/26)`,
      ogDescription: `Calculate take-home pay for ${formatCurrency(salary)} salary in the UK. ${formatCurrency(englandResults.netPay.monthly)} per month after tax.`,
      canonical: `${defaultMeta.siteUrl}/salary/${salary}`,
    });
  }, [salary, isValidSalary]);

  // Early return for invalid salary (after hooks)
  if (!isValidSalary) {
    return (
      <div className="text-center space-y-4 py-12">
        <h1 className="text-4xl font-bold text-gray-900">Invalid Salary Amount</h1>
        <p className="text-gray-600">Please enter a valid salary amount between £1 and £1,000,000.</p>
        <Link to="/" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
          Go to Calculator
        </Link>
      </div>
    );
  }

  // Pre-calculate for both regions for display
  const englandResults = calculateTakeHomePay({
    annualSalary: salary,
    region: 'england',
    pensionPercent: 0,
    studentLoanPlan: 'none'
  });

  const scotlandResults = calculateTakeHomePay({
    annualSalary: salary,
    region: 'scotland',
    pensionPercent: 0,
    studentLoanPlan: 'none'
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
          {formatCurrency(salary)} Salary After Tax
        </h1>
        <p className="text-lg text-gray-600">
          Take-home pay calculation for {formatCurrency(salary)} annual salary in the UK (2025/26 tax year)
        </p>
      </motion.section>

      {/* Quick Results */}
      <motion.section 
        className="grid md:grid-cols-2 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">England, Wales & Northern Ireland</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Annual Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(englandResults.netPay.annual)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(englandResults.netPay.monthly)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Weekly Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(englandResults.netPay.weekly)}</span>
            </div>
            <hr className="my-3" />
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-red-600">
                <span>Income Tax:</span>
                <span>-{formatCurrency(englandResults.incomeTax)}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>National Insurance:</span>
                <span>-{formatCurrency(englandResults.nationalInsurance)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Scotland</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Annual Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(scotlandResults.netPay.annual)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(scotlandResults.netPay.monthly)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Weekly Take-Home:</span>
              <span className="font-bold text-green-600">{formatCurrency(scotlandResults.netPay.weekly)}</span>
            </div>
            <hr className="my-3" />
            <div className="text-sm space-y-1">
              <div className="flex justify-between text-red-600">
                <span>Income Tax:</span>
                <span>-{formatCurrency(scotlandResults.incomeTax)}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>National Insurance:</span>
                <span>-{formatCurrency(scotlandResults.nationalInsurance)}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Detailed Calculator */}
      <TaxCalculator initialSalary={salary} />

      {/* Comprehensive Salary Analysis */}
      <motion.section 
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Comprehensive Analysis: {formatCurrency(salary)} Salary</h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <h3 className="font-semibold mb-3 text-blue-900">Tax Breakdown</h3>
              <p className="mb-2 leading-relaxed">
                <strong>Tax Bracket:</strong> This salary puts you in the {salary > 125140 ? 'additional' : salary > 50270 ? 'higher' : 'basic'} rate tax bracket.
              </p>
              {salary > 50270 && salary <= 125140 && (
                <p className="mb-2 leading-relaxed">
                  <strong>Higher Rate:</strong> You pay 40% tax on earnings above £50,270 (42% in Scotland above £43,662). This means a significant portion of your salary is taxed at the higher rate.
                </p>
              )}
              {salary > 100000 && salary <= 125140 && (
                <p className="mb-2 leading-relaxed text-amber-700 font-medium">
                  <strong>⚠️ Personal Allowance Taper:</strong> Your personal allowance is being reduced. You're in the "60% tax trap" zone. Consider increasing pension contributions to stay below £100,000.
                </p>
              )}
              {salary > 125140 && (
                <p className="mb-2 leading-relaxed text-rose-700 font-medium">
                  <strong>Additional Rate:</strong> You pay 45% tax on earnings above £125,140. Your personal allowance has been completely eliminated.
                </p>
              )}
            </div>
            <div>
              <h3 className="font-semibold mb-3 text-blue-900">Earning Breakdown</h3>
              <p className="mb-2 leading-relaxed">
                <strong>Hourly Rate:</strong> Approximately £{(salary / (37.5 * 52)).toFixed(2)} per hour (assuming 37.5 hours/week, standard full-time).
              </p>
              <p className="mb-2 leading-relaxed">
                <strong>Daily Rate:</strong> About £{(salary / 260).toFixed(0)} per working day (assuming 260 working days/year, excluding weekends and holidays).
              </p>
              <p className="mb-2 leading-relaxed">
                <strong>Weekly Gross:</strong> {formatCurrency(salary / 52)} per week before deductions.
              </p>
            </div>
          </div>
        </div>

        {/* Tax Optimization Tips */}
        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Optimization Strategies for {formatCurrency(salary)}</h3>
          <div className="space-y-4 text-sm text-gray-700">
            {salary >= 100000 && salary <= 125140 && (
              <div className="bg-white rounded-lg p-4 border border-emerald-300">
                <h4 className="font-semibold mb-2 text-emerald-900">⚠️ Critical: Personal Allowance Taper Zone</h4>
                <p className="mb-2 leading-relaxed">
                  You're in the "60% tax trap" between £100,000 and £125,140. Every £2 you earn above £100,000 reduces your personal allowance by £1, creating an effective 60% tax rate.
                </p>
                <p className="font-medium text-emerald-800">
                  <strong>Recommended Action:</strong> Consider increasing pension contributions to bring your taxable income below £100,000. This could save you thousands in tax.
                </p>
              </div>
            )}
            {salary > 50270 && (
              <div>
                <h4 className="font-semibold mb-2 text-emerald-900">Higher Rate Taxpayer Benefits:</h4>
                <ul className="space-y-2 leading-relaxed">
                  <li>• <strong>Pension Tax Relief:</strong> You get 40% tax relief on pension contributions (45% if additional rate). Contributing £1,000 to your pension only costs you £600 in take-home pay.</li>
                  <li>• <strong>Annual Pension Allowance:</strong> You can contribute up to £60,000 per year (or 100% of earnings, whichever is lower) and receive tax relief.</li>
                  <li>• <strong>Salary Sacrifice:</strong> Consider salary sacrifice schemes for benefits like childcare vouchers, cycle-to-work schemes, or electric vehicles - you save both income tax and National Insurance.</li>
                </ul>
              </div>
            )}
            <div>
              <h4 className="font-semibold mb-2 text-emerald-900">General Tax Efficiency Tips:</h4>
              <ul className="space-y-2 leading-relaxed">
                <li>• <strong>ISA Contributions:</strong> Maximize your £20,000 annual ISA allowance for tax-free savings and investments.</li>
                <li>• <strong>Marriage Allowance:</strong> If you're married and one partner earns less than the personal allowance, you can transfer up to £1,260 of personal allowance.</li>
                <li>• <strong>Charitable Giving:</strong> Higher rate taxpayers can claim additional tax relief on charitable donations through Gift Aid.</li>
                <li>• <strong>Professional Expenses:</strong> Claim all eligible work-related expenses and professional subscriptions.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Financial Planning */}
        <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Planning for {formatCurrency(salary)}</h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div>
              <h4 className="font-semibold mb-2 text-indigo-900">Budgeting Guidelines:</h4>
              <ul className="space-y-2 leading-relaxed">
                <li>• <strong>Housing:</strong> Aim to spend no more than 30-35% of take-home pay on housing costs</li>
                <li>• <strong>Savings:</strong> Target saving 20% of take-home pay (emergency fund + investments)</li>
                <li>• <strong>Pension:</strong> Contribute at least 10-15% of salary (including employer contributions)</li>
                <li>• <strong>Debt:</strong> Prioritize high-interest debt repayment before investing</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2 text-indigo-900">Take-Home Pay Allocation:</h4>
              <p className="mb-2 leading-relaxed">
                With a monthly take-home of approximately {formatCurrency(englandResults.netPay.monthly)}, consider this allocation:
              </p>
              <ul className="space-y-1 leading-relaxed">
                <li>• Housing: {formatCurrency(englandResults.netPay.monthly * 0.30)} (30%)</li>
                <li>• Living expenses: {formatCurrency(englandResults.netPay.monthly * 0.40)} (40%)</li>
                <li>• Savings & investments: {formatCurrency(englandResults.netPay.monthly * 0.20)} (20%)</li>
                <li>• Discretionary: {formatCurrency(englandResults.netPay.monthly * 0.10)} (10%)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Comparison with Other Salaries */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">How {formatCurrency(salary)} Compares</h3>
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              <strong>UK Average Salary:</strong> The median full-time salary in the UK is approximately £35,000. Your salary of {formatCurrency(salary)} is {salary > 35000 ? `${((salary / 35000 - 1) * 100).toFixed(0)}%` : `${((1 - salary / 35000) * 100).toFixed(0)}%`} {salary > 35000 ? 'above' : 'below'} the UK median.
            </p>
            {salary >= 50000 && (
              <p>
                <strong>High Earner Status:</strong> You're in the top {salary >= 100000 ? '5%' : salary >= 70000 ? '10%' : '20%'} of UK earners. This gives you significant opportunities for tax-efficient savings and investments.
              </p>
            )}
            <p>
              <strong>Take-Home Comparison:</strong> Your annual take-home of {formatCurrency(englandResults.netPay.annual)} represents {((englandResults.netPay.annual / salary) * 100).toFixed(1)}% of your gross salary after Income Tax and National Insurance.
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default SalaryPage;